import React, { useState, useMemo } from "react";

// ── Data ─────────────────────────────────────────────────────────────────────
const MODULES = [
  {
    id: "overview", label: "Overview & Auth", section: "Getting Started",
    color: "#5F5E5A", content: "overview",
  },
  {
    id: "gateway", label: "Unified KYC Gateway", section: "Gateway",
    color: "#D85A30", content: "gateway",
    url: "/account/planapi/verify",
    params: [
      { name: "user_id", type: "integer", req: true, desc: "Authenticated user ID" },
      { name: "pin", type: "string", req: true, desc: "4-digit transaction PIN" },
      { name: "service", type: "string", req: true, desc: "Service key e.g. PAN_VERIFY, AADHAAR_OTP" },
      { name: "amount", type: "number", req: false, desc: "Override charge amount (default 0)" },
    ],
    request: `{\n  "user_id": 1024,\n  "pin": "1234",\n  "service": "PAN_VERIFY",\n  "pan_number": "ABCDE1234F",\n  "amount": 0\n}`,
    success: `{\n  "status": true,\n  "message": "Verification successful",\n  "data": {\n    "Status": "Success",\n    "Errorcode": 200,\n    "response": {\n      "pan_number": "ABCDE1234F",\n      "registered_name": "RAHUL SHARMA",\n      "status": "VALID",\n      "type": "Individual"\n    }\n  },\n  "ref_id": "9812401923"\n}`,
    error: `{\n  "status": false,\n  "message": "Invalid Parameters",\n  "Errorcode": 104\n}`,
  },
  // --- Aadhaar ---
  {
    id: "aadhaar_otp", label: "Aadhaar: Send OTP", section: "Aadhaar",
    color: "#185FA5", content: "endpoint",
    url: "/account/planapi/aadhaar/send_otp",
    params: [
      { name: "aadhaar_number", type: "string", req: true, desc: "12-digit Aadhaar number" },
      { name: "user_id", type: "integer", req: false, desc: "Authenticated user ID" },
      { name: "pin", type: "string", req: false, desc: "4-digit transaction PIN" },
    ],
    request: `{\n  "aadhaar_number": "123456789012"\n}`,
    success: `{\n  "status": true,\n  "message": "OTP has been sent to linked mobile number",\n  "data": {\n    "Errorcode": 200,\n    "status": "success",\n    "response": {\n      "ref_id": "7891234567",\n      "message": "OTP sent successfully"\n    }\n  },\n  "ref_id": "7891234567"\n}`,
    error: `{\n  "status": false,\n  "message": "Invalid Aadhaar Number",\n  "Errorcode": 104\n}`,
  },
  {
    id: "aadhaar_verify", label: "Aadhaar: Verify OTP", section: null,
    color: "#185FA5", content: "endpoint",
    url: "/account/planapi/aadhaar/verify_otp",
    params: [
      { name: "ref_id", type: "string", req: true, desc: "Reference ID from send_otp" },
      { name: "otp", type: "string", req: true, desc: "6-digit OTP received by customer" },
    ],
    request: `{\n  "ref_id": "7891234567",\n  "otp": "456123"\n}`,
    success: `{\n  "status": true,\n  "message": "Aadhaar verified successfully",\n  "data": {\n    "Errorcode": 100,\n    "status": "Success",\n    "response": {\n      "name": "Rahul Sharma",\n      "gender": "M",\n      "dob": "1994-08-15",\n      "care_of": "S/O Suresh Sharma",\n      "address": "104 Green Park, New Delhi 110016"\n    }\n  }\n}`,
    error: `{\n  "status": false,\n  "message": "Invalid OTP",\n  "Errorcode": 104\n}`,
  },
  {
    id: "aadhaar_ocr", label: "Aadhaar: OCR", section: null,
    color: "#185FA5", content: "endpoint", encoding: "multipart/form-data",
    url: "/account/planapi/aadhaar/ocr",
    params: [{ name: "image", type: "file", req: true, desc: "Image file of Aadhaar card front/back" }],
    request: `FormData: { "image": <file> }`,
    success: `{\n  "status": true,\n  "data": {\n    "Errorcode": 200,\n    "response": {\n      "Name": "RAHUL SHARMA",\n      "Aadhaar_Number": "XXXXXXXX9012",\n      "DOB": "15/08/1994",\n      "Gender": "MALE"\n    }\n  }\n}`,
    error: `{\n  "status": false,\n  "message": "Could not extract data",\n  "Errorcode": 104\n}`,
  },
  {
    id: "aadhaar_mask", label: "Aadhaar: Masking", section: null,
    color: "#185FA5", content: "endpoint", encoding: "multipart/form-data",
    url: "/account/planapi/aadhaar/masking",
    params: [{ name: "image", type: "file", req: true, desc: "Image file of Aadhaar card" }],
    request: `FormData: { "image": <file> }`,
    success: `{\n  "status": true,\n  "data": {\n    "Errorcode": 200,\n    "response": {\n      "masked_image_url": "https://planapi.in/uploads/masked_abc123.jpg",\n      "aadhaar_last_four": "9012"\n    }\n  }\n}`,
    error: `{\n  "status": false,\n  "message": "Processing failed",\n  "Errorcode": 104\n}`,
  },
  {
    id: "aadhaar_pan_link", label: "Aadhaar–PAN Link Check", section: null,
    color: "#185FA5", content: "endpoint",
    url: "/account/planapi/aadhaar/pan_link_check",
    params: [
      { name: "aadhaar_number", type: "string", req: true, desc: "12-digit Aadhaar" },
      { name: "pan_number", type: "string", req: true, desc: "10-character PAN" },
    ],
    request: `{\n  "aadhaar_number": "123456789012",\n  "pan_number": "ABCDE1234F"\n}`,
    success: `{\n  "status": true,\n  "data": {\n    "Errorcode": 200,\n    "response": {\n      "is_linked": true,\n      "message": "Aadhaar is linked with PAN"\n    }\n  }\n}`,
    error: `{\n  "status": false,\n  "message": "Not linked",\n  "Errorcode": 211\n}`,
  },
  // --- DigiLocker ---
  {
    id: "digi_init", label: "DigiLocker: Initialize", section: "DigiLocker",
    color: "#0E7C6E", content: "endpoint",
    url: "/account/planapi/digilocker/initialize",
    params: [],
    request: `{}`,
    success: `{\n  "status": true,\n  "data": {\n    "Errorcode": 200,\n    "response": {\n      "access_url": "https://api.digitallocker.gov.in/...",\n      "session_id": "DGL_SESSION_894129"\n    }\n  }\n}`,
    error: `{\n  "status": false,\n  "Errorcode": 101\n}`,
  },
  {
    id: "digi_status", label: "DigiLocker: Status", section: null,
    color: "#0E7C6E", content: "endpoint",
    url: "/account/planapi/digilocker/status",
    params: [{ name: "session_id", type: "string", req: true, desc: "DigiLocker session identifier" }],
    request: `{\n  "session_id": "DGL_SESSION_894129"\n}`,
    success: `{\n  "status": true,\n  "data": {\n    "Errorcode": 200,\n    "response": {\n      "status": "COMPLETED",\n      "user_authorized": true\n    }\n  }\n}`,
    error: `{\n  "status": false,\n  "message": "Session expired",\n  "Errorcode": 104\n}`,
  },
  {
    id: "digi_aadhaar", label: "DigiLocker: Download Aadhaar", section: null,
    color: "#0E7C6E", content: "endpoint",
    url: "/account/planapi/digilocker/download_aadhaar",
    params: [{ name: "session_id", type: "string", req: true, desc: "Verified DigiLocker session ID" }],
    request: `{\n  "session_id": "DGL_SESSION_894129"\n}`,
    success: `{\n  "status": true,\n  "data": {\n    "Errorcode": 200,\n    "response": {\n      "Full_Name": "RAHUL SHARMA",\n      "DOB": "15-08-1994",\n      "Gender": "M",\n      "Address": "New Delhi, India"\n    }\n  }\n}`,
    error: `{\n  "status": false,\n  "Errorcode": 104\n}`,
  },
  // --- PAN ---
  {
    id: "pan_verify", label: "PAN: Basic Verify", section: "PAN Card",
    color: "#3B6D11", content: "endpoint",
    url: "/account/planapi/pan/verify",
    params: [{ name: "pan_number", type: "string", req: true, desc: "10-character PAN" }],
    request: `{\n  "pan_number": "ABCDE1234F"\n}`,
    success: `{\n  "status": true,\n  "data": {\n    "Errorcode": 200,\n    "response": {\n      "pan_number": "ABCDE1234F",\n      "registered_name": "RAHUL SHARMA",\n      "status": "VALID",\n      "type": "Individual"\n    }\n  }\n}`,
    error: `{\n  "status": false,\n  "message": "Invalid PAN",\n  "Errorcode": 104\n}`,
  },
  {
    id: "pan_details", label: "PAN: Comprehensive Details", section: null,
    color: "#3B6D11", content: "endpoint",
    url: "/account/planapi/pan/details",
    params: [{ name: "pan_number", type: "string", req: true, desc: "10-character PAN" }],
    request: `{\n  "pan_number": "ABCDE1234F"\n}`,
    success: `{\n  "status": true,\n  "data": {\n    "Errorcode": 100,\n    "response": {\n      "pan_number": "ABCDE1234F",\n      "full_name": "RAHUL SHARMA",\n      "father_name": "SURESH SHARMA",\n      "dob": "15/08/1994",\n      "aadhaar_linked": true,\n      "category": "Individual",\n      "pan_status": "Existing and Valid"\n    }\n  }\n}`,
    error: `{\n  "status": false,\n  "Errorcode": 211\n}`,
  },
  {
    id: "pan_ocr", label: "PAN: OCR", section: null,
    color: "#3B6D11", content: "endpoint", encoding: "multipart/form-data",
    url: "/account/planapi/pan/ocr",
    params: [{ name: "image", type: "file", req: true, desc: "Image of PAN card" }],
    request: `FormData: { "image": <file> }`,
    success: `{\n  "status": true,\n  "data": {\n    "Errorcode": 200,\n    "response": {\n      "PAN": "ABCDE1234F",\n      "Name": "RAHUL SHARMA",\n      "Father_Name": "SURESH SHARMA",\n      "DOB": "15/08/1994"\n    }\n  }\n}`,
    error: `{\n  "status": false,\n  "Errorcode": 104\n}`,
  },
  {
    id: "pan_gst", label: "PAN: by GST Number", section: null,
    color: "#3B6D11", content: "endpoint",
    url: "/account/planapi/pan/by_gst",
    params: [{ name: "gstin", type: "string", req: true, desc: "15-digit GSTIN" }],
    request: `{\n  "gstin": "07AAAAA0000A1Z5"\n}`,
    success: `{\n  "status": true,\n  "data": {\n    "Errorcode": 200,\n    "response": {\n      "pan": "AAAAA0000A",\n      "entity_name": "XYZ ENTERPRISES"\n    }\n  }\n}`,
    error: `{\n  "status": false,\n  "Errorcode": 104\n}`,
  },
  {
    id: "pan_bank", label: "PAN: Bank Linkage", section: null,
    color: "#3B6D11", content: "endpoint",
    url: "/account/planapi/pan/to_bank_linkage",
    params: [
      { name: "pan_number", type: "string", req: true, desc: "10-character PAN" },
      { name: "account_number", type: "string", req: true, desc: "Bank account number" },
      { name: "ifsc", type: "string", req: true, desc: "11-character IFSC code" },
    ],
    request: `{\n  "pan_number": "ABCDE1234F",\n  "account_number": "12345678901234",\n  "ifsc": "HDFC0000123"\n}`,
    success: `{\n  "status": true,\n  "data": {\n    "Errorcode": 200,\n    "response": {\n      "is_linked": true,\n      "name_match_score": 95,\n      "message": "Account matches PAN holder"\n    }\n  }\n}`,
    error: `{\n  "status": false,\n  "Errorcode": 211\n}`,
  },
  // --- Bank ---
  {
    id: "bank_verify", label: "Bank: Account Verify", section: "Bank & IFSC",
    color: "#534AB7", content: "endpoint",
    url: "/account/planapi/bank/verify",
    params: [
      { name: "account_number", type: "string", req: true, desc: "Bank account number" },
      { name: "ifsc", type: "string", req: true, desc: "Bank IFSC code" },
    ],
    request: `{\n  "account_number": "12345678901234",\n  "ifsc": "HDFC0000123"\n}`,
    success: `{\n  "status": true,\n  "data": {\n    "Errorcode": 200,\n    "response": {\n      "account_number": "12345678901234",\n      "ifsc": "HDFC0000123",\n      "beneficiary_name": "RAHUL SHARMA",\n      "bank_name": "HDFC BANK",\n      "utr": "HDFC481029410"\n    }\n  }\n}`,
    error: `{\n  "status": false,\n  "message": "Invalid bank details",\n  "Errorcode": 104\n}`,
  },
  {
    id: "bank_live", label: "Bank: Penny Drop (Live)", section: null,
    color: "#534AB7", content: "endpoint",
    url: "/account/planapi/bank/verify_live",
    params: [
      { name: "account_number", type: "string", req: true, desc: "Bank account number" },
      { name: "ifsc", type: "string", req: true, desc: "IFSC code" },
    ],
    request: `{\n  "account_number": "12345678901234",\n  "ifsc": "HDFC0000123"\n}`,
    success: `{\n  "status": true,\n  "data": {\n    "Errorcode": 100,\n    "response": {\n      "beneficiary_name": "RAHUL SHARMA",\n      "status": "ACCOUNT_EXISTS",\n      "transaction_ref": "TXN_LIVE_90129",\n      "amount_credited": 1.00\n    }\n  }\n}`,
    error: `{\n  "status": false,\n  "Errorcode": 211\n}`,
  },
  {
    id: "ifsc", label: "Bank: Details by IFSC", section: null,
    color: "#534AB7", content: "endpoint",
    url: "/account/planapi/bank/details_by_ifsc",
    params: [{ name: "ifsc", type: "string", req: true, desc: "11-digit IFSC" }],
    request: `{\n  "ifsc": "SBIN0001234"\n}`,
    success: `{\n  "status": true,\n  "data": {\n    "Errorcode": 200,\n    "response": {\n      "bank": "STATE BANK OF INDIA",\n      "branch": "CONNAUGHT PLACE",\n      "city": "NEW DELHI",\n      "state": "DELHI",\n      "micr": "110002001"\n    }\n  }\n}`,
    error: `{\n  "status": false,\n  "Errorcode": 104\n}`,
  },
  // --- UPI ---
  {
    id: "upi_verify", label: "UPI: Verify VPA", section: "UPI / VPA",
    color: "#BA7517", content: "endpoint",
    url: "/account/planapi/upi/verify",
    params: [{ name: "vpa", type: "string", req: true, desc: "UPI ID e.g. user@okhdfcbank" }],
    request: `{\n  "vpa": "rahul@okhdfcbank"\n}`,
    success: `{\n  "status": true,\n  "data": {\n    "Errorcode": 200,\n    "response": {\n      "vpa": "rahul@okhdfcbank",\n      "account_holder_name": "RAHUL SHARMA",\n      "status": "ACTIVE"\n    }\n  }\n}`,
    error: `{\n  "status": false,\n  "Errorcode": 104\n}`,
  },
  {
    id: "upi_validate", label: "UPI: Validate", section: null,
    color: "#BA7517", content: "endpoint",
    url: "/account/planapi/upi/validate",
    params: [{ name: "vpa", type: "string", req: true, desc: "UPI ID" }],
    request: `{\n  "vpa": "rahul@okhdfcbank"\n}`,
    success: `{\n  "status": true,\n  "data": { "Errorcode": 200, "response": { "is_valid": true, "payee_name": "RAHUL SHARMA" } }\n}`,
    error: `{\n  "status": false,\n  "Errorcode": 104\n}`,
  },
  {
    id: "upi_mobile", label: "UPI: Mobile to VPA", section: null,
    color: "#BA7517", content: "endpoint",
    url: "/account/planapi/upi/mobile_to_vpa",
    params: [{ name: "mobile", type: "string", req: true, desc: "10-digit mobile number" }],
    request: `{\n  "mobile": "9876543210"\n}`,
    success: `{\n  "status": true,\n  "data": {\n    "Errorcode": 200,\n    "response": { "vpa_list": ["9876543210@paytm", "9876543210@upi"] }\n  }\n}`,
    error: `{\n  "status": false,\n  "Errorcode": 104\n}`,
  },
  // --- GST ---
  {
    id: "gst_verify", label: "GST: Verify GSTIN", section: "GST",
    color: "#056839", content: "endpoint",
    url: "/account/planapi/gst/verify",
    params: [{ name: "gstin", type: "string", req: true, desc: "15-digit GSTIN" }],
    request: `{\n  "gstin": "07AAAAA0000A1Z5"\n}`,
    success: `{\n  "status": true,\n  "data": {\n    "Errorcode": 200,\n    "response": {\n      "gstin": "07AAAAA0000A1Z5",\n      "legal_name": "ACME TRADING CORP",\n      "trade_name": "ACME SOLUTIONS",\n      "registration_date": "01/07/2017",\n      "status": "Active",\n      "taxpayer_type": "Regular",\n      "state": "Delhi"\n    }\n  }\n}`,
    error: `{\n  "status": false,\n  "Errorcode": 104\n}`,
  },
  {
    id: "gst_returns", label: "GST: Return Status", section: null,
    color: "#056839", content: "endpoint",
    url: "/account/planapi/gst/return_status",
    params: [
      { name: "gstin", type: "string", req: true, desc: "15-digit GSTIN" },
      { name: "fy", type: "string", req: false, desc: "Financial year e.g. 2023-24" },
    ],
    request: `{\n  "gstin": "07AAAAA0000A1Z5",\n  "fy": "2023-24"\n}`,
    success: `{\n  "status": true,\n  "data": {\n    "Errorcode": 200,\n    "response": {\n      "filing_status": [\n        { "return_type": "GSTR-3B", "period": "March 2024", "status": "Filed" },\n        { "return_type": "GSTR-1", "period": "March 2024", "status": "Filed" }\n      ]\n    }\n  }\n}`,
    error: `{\n  "status": false,\n  "Errorcode": 104\n}`,
  },
  // --- Voter ---
  {
    id: "voter_basic", label: "Voter ID: Basic", section: "Voter ID",
    color: "#7B3FA5", content: "endpoint",
    url: "/account/planapi/voter/verify",
    params: [{ name: "epic_number", type: "string", req: true, desc: "Voter ID EPIC number" }],
    request: `{\n  "epic_number": "ABC1234567"\n}`,
    success: `{\n  "status": true,\n  "data": {\n    "Errorcode": 200,\n    "response": {\n      "epic_no": "ABC1234567",\n      "holder_name": "RAHUL SHARMA",\n      "gender": "M",\n      "age": 30,\n      "state": "DELHI",\n      "assembly_constituency": "New Delhi"\n    }\n  }\n}`,
    error: `{\n  "status": false,\n  "Errorcode": 104\n}`,
  },
  {
    id: "voter_advance", label: "Voter ID: Advance", section: null,
    color: "#7B3FA5", content: "endpoint",
    url: "/account/planapi/voter/verify_advance",
    params: [{ name: "epic_number", type: "string", req: true, desc: "EPIC number" }],
    request: `{\n  "epic_number": "ABC1234567"\n}`,
    success: `{\n  "status": true,\n  "data": {\n    "Errorcode": 200,\n    "response": {\n      "fullName": "RAHUL SHARMA",\n      "relativeName": "SURESH SHARMA",\n      "dob": "1994-08-15",\n      "pollingStation": "Govt Boys Sr Sec School"\n    }\n  }\n}`,
    error: `{\n  "status": false,\n  "Errorcode": 104\n}`,
  },
  // --- Passport ---
  {
    id: "passport", label: "Passport Verification", section: "Passport",
    color: "#A3320D", content: "endpoint",
    url: "/account/planapi/passport/verify",
    params: [
      { name: "file_number", type: "string", req: true, desc: "15-character Passport file number" },
      { name: "dob", type: "string", req: true, desc: "Date of birth (YYYY-MM-DD)" },
    ],
    request: `{\n  "file_number": "DEL123456789012",\n  "dob": "1994-08-15"\n}`,
    success: `{\n  "status": true,\n  "data": {\n    "Errorcode": 200,\n    "response": {\n      "holder_name": "RAHUL SHARMA",\n      "file_number": "DEL123456789012",\n      "passport_number": "P1234567",\n      "application_date": "2020-01-10",\n      "status": "Passport has been dispatched"\n    }\n  }\n}`,
    error: `{\n  "status": false,\n  "Errorcode": 104\n}`,
  },
  // --- DL ---
  {
    id: "dl_verify", label: "Driving Licence Verify", section: "Driving Licence",
    color: "#8B6309", content: "endpoint",
    url: "/account/planapi/dl/verify",
    params: [
      { name: "dl_number", type: "string", req: true, desc: "DL number e.g. DL-0420110012345" },
      { name: "dob", type: "string", req: true, desc: "Date of birth (YYYY-MM-DD)" },
    ],
    request: `{\n  "dl_number": "DL-0420110012345",\n  "dob": "1994-08-15"\n}`,
    success: `{\n  "status": true,\n  "data": {\n    "Errorcode": 200,\n    "response": {\n      "dl_number": "DL-0420110012345",\n      "holder_name": "RAHUL SHARMA",\n      "issue_date": "2011-05-10",\n      "valid_till": "2031-05-09",\n      "vehicle_classes": ["MCWG","LMV"],\n      "status": "Active"\n    }\n  }\n}`,
    error: `{\n  "status": false,\n  "Errorcode": 104\n}`,
  },
  // --- MCA ---
  {
    id: "mca_search", label: "MCA: Company Search", section: "MCA / Company",
    color: "#2D6A8E", content: "endpoint",
    url: "/account/planapi/mca/search",
    params: [{ name: "company_name", type: "string", req: true, desc: "Company name to search" }],
    request: `{\n  "company_name": "INFOSYS LIMITED"\n}`,
    success: `{\n  "status": true,\n  "data": {\n    "Errorcode": 200,\n    "response": {\n      "companies": [\n        { "cin": "L85110KA1981PLC013115", "name": "INFOSYS LIMITED", "state": "Karnataka" }\n      ]\n    }\n  }\n}`,
    error: `{\n  "status": false,\n  "Errorcode": 104\n}`,
  },
  {
    id: "mca_cin", label: "MCA: CIN Search", section: null,
    color: "#2D6A8E", content: "endpoint",
    url: "/account/planapi/mca/cin_search",
    params: [{ name: "cin", type: "string", req: true, desc: "21-digit Corporate Identification Number" }],
    request: `{\n  "cin": "L85110KA1981PLC013115"\n}`,
    success: `{\n  "status": true,\n  "data": {\n    "Errorcode": 200,\n    "response": {\n      "cin": "L85110KA1981PLC013115",\n      "company_name": "INFOSYS LIMITED",\n      "roc": "ROC Bangalore",\n      "incorporation_date": "02/07/1981",\n      "authorized_capital": 2400000000,\n      "status": "Active"\n    }\n  }\n}`,
    error: `{\n  "status": false,\n  "Errorcode": 104\n}`,
  },
  {
    id: "din_details", label: "MCA: DIN Details", section: null,
    color: "#2D6A8E", content: "endpoint",
    url: "/account/planapi/mca/din_details",
    params: [{ name: "din", type: "string", req: true, desc: "8-digit Director Identification Number" }],
    request: `{\n  "din": "00123456"\n}`,
    success: `{\n  "status": true,\n  "data": {\n    "Errorcode": 200,\n    "response": {\n      "din": "00123456",\n      "full_name": "RAHUL SHARMA",\n      "directorships": [\n        { "cin": "U72200DL2018PTC123456", "company": "TECH SERVICES PVT LTD" }\n      ]\n    }\n  }\n}`,
    error: `{\n  "status": false,\n  "Errorcode": 104\n}`,
  },
  // --- TAN ---
  {
    id: "tan_verify", label: "TAN Verification", section: "TAN",
    color: "#6B4F9E", content: "endpoint",
    url: "/account/planapi/tan/verify",
    params: [{ name: "tan_number", type: "string", req: true, desc: "10-character TAN number" }],
    request: `{\n  "tan_number": "DELR12345A"\n}`,
    success: `{\n  "status": true,\n  "data": {\n    "Errorcode": 200,\n    "response": {\n      "tan": "DELR12345A",\n      "name": "ACME PVT LTD",\n      "category": "Company",\n      "address": "Connaught Place, New Delhi"\n    }\n  }\n}`,
    error: `{\n  "status": false,\n  "Errorcode": 104\n}`,
  },
  // --- RC ---
  {
    id: "rc_verify", label: "RC: Vehicle Verify", section: "Vehicle / RC",
    color: "#8B3A62", content: "endpoint",
    url: "/account/planapi/rc/verify",
    params: [{ name: "vehicle_number", type: "string", req: true, desc: "Vehicle registration number e.g. DL01AB1234" }],
    request: `{\n  "vehicle_number": "DL01AB1234"\n}`,
    success: `{\n  "status": true,\n  "data": {\n    "Errorcode": 200,\n    "response": {\n      "vehicle_number": "DL01AB1234",\n      "owner_name": "RAHUL SHARMA",\n      "maker_model": "HYUNDAI CRETA",\n      "fuel_type": "PETROL",\n      "registration_date": "2021-04-10",\n      "insurance_upto": "2025-04-09",\n      "status": "ACTIVE"\n    }\n  }\n}`,
    error: `{\n  "status": false,\n  "Errorcode": 104\n}`,
  },
  {
    id: "rc_challan", label: "RC: Challan Check", section: null,
    color: "#8B3A62", content: "endpoint",
    url: "/account/planapi/rc/challan",
    params: [
      { name: "vehicle_number", type: "string", req: true, desc: "Vehicle registration number" },
      { name: "chassis_number", type: "string", req: false, desc: "Last 5 digits of chassis" },
    ],
    request: `{\n  "vehicle_number": "DL01AB1234"\n}`,
    success: `{\n  "status": true,\n  "data": {\n    "Errorcode": 200,\n    "response": {\n      "vehicle_number": "DL01AB1234",\n      "challans": [\n        {\n          "challan_number": "DL12345678",\n          "date": "2024-02-15",\n          "amount": 1000,\n          "status": "Pending",\n          "offense": "Over Speeding"\n        }\n      ]\n    }\n  }\n}`,
    error: `{\n  "status": false,\n  "Errorcode": 104\n}`,
  },
  // --- MSME ---
  {
    id: "udyog", label: "Udyog Aadhaar / MSME", section: "MSME",
    color: "#4A7C59", content: "endpoint",
    url: "/account/planapi/udyog_aadhaar/verify",
    params: [{ name: "udyam_number", type: "string", req: true, desc: "e.g. UDYAM-DL-01-0012345" }],
    request: `{\n  "udyam_number": "UDYAM-DL-01-0012345"\n}`,
    success: `{\n  "status": true,\n  "data": {\n    "Errorcode": 200,\n    "response": {\n      "udyam_number": "UDYAM-DL-01-0012345",\n      "enterprise_name": "SHARMA ENTERPRISES",\n      "enterprise_type": "Micro",\n      "major_activity": "Services",\n      "commencement_date": "2020-03-01"\n    }\n  }\n}`,
    error: `{\n  "status": false,\n  "Errorcode": 104\n}`,
  },
];

const ERROR_CODES = [
  { code: "100", meaning: "Verified / Success", billed: true },
  { code: "200", meaning: "Verified / Success", billed: true },
  { code: "211", meaning: "Record Not Found (valid check executed)", billed: true },
  { code: "101", meaning: "Authentication Failure (Invalid Token/User/Pass)", billed: false },
  { code: "104", meaning: "Missing or Invalid Parameters", billed: false },
  { code: "404", meaning: "Endpoint Not Found", billed: false },
];

// ── Styles ────────────────────────────────────────────────────────────────────
const S = {
  badge: (type) => ({
    display: "inline-block", padding: "2px 8px", borderRadius: 4,
    fontSize: 10, fontWeight: 700, letterSpacing: ".04em",
    background: type === "required" ? "#FEECEC" : "#F0F4F8",
    color: type === "required" ? "#B91C1C" : "#64748B",
  }),
  methodBadge: {
    display: "inline-block", padding: "3px 10px", borderRadius: 5,
    fontSize: 11, fontWeight: 700, background: "#EAF3DE", color: "#3B6D11",
    letterSpacing: ".05em",
  },
  encodingBadge: {
    display: "inline-block", padding: "2px 8px", borderRadius: 4,
    fontSize: 10, fontWeight: 600, background: "#EEF2FF", color: "#4F46E5",
  },
  codeWrap: {
    background: "#0F172A", borderRadius: 10, padding: "16px 18px",
    fontFamily: "'Fira Code', 'Courier New', monospace", fontSize: 12,
    lineHeight: 1.75, overflowX: "auto", position: "relative",
    color: "#E2E8F0", whiteSpace: "pre",
  },
  th: {
    padding: "9px 12px", fontSize: 11, fontWeight: 600, color: "#64748B",
    textAlign: "left", borderBottom: "1px solid #E2E8F0", background: "#F8FAFC",
  },
  td: { padding: "10px 12px", fontSize: 12, borderBottom: "1px solid #F1F5F9", verticalAlign: "top" },
  sectionLabel: {
    fontSize: 10, fontWeight: 700, letterSpacing: ".08em",
    textTransform: "uppercase", color: "#94A3B8", padding: "14px 16px 5px",
  },
  tab: (active) => ({
    padding: "7px 14px", fontSize: 12, fontWeight: active ? 600 : 400,
    borderBottom: active ? "2px solid #185FA5" : "2px solid transparent",
    color: active ? "#185FA5" : "#64748B", cursor: "pointer", transition: "all .15s",
    background: "none", border: "none", borderBottom: active ? "2px solid #185FA5" : "2px solid transparent",
  }),
};

// ── Sub-components ─────────────────────────────────────────────────────────────
function CopyButton({ text, style = {} }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      onClick={() => { navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 1800); }}
      style={{
        position: "absolute", top: 8, right: 10, background: copied ? "#1E3A1E" : "#1E293B",
        border: "1px solid #334155", borderRadius: 5, padding: "3px 9px",
        fontSize: 11, color: copied ? "#86EFAC" : "#94A3B8", cursor: "pointer", ...style,
      }}
    >
      {copied ? "✓ Copied" : "Copy"}
    </button>
  );
}

function CodeBlock({ code, label }) {
  return (
    <div style={{ marginBottom: 16 }}>
      {label && <div style={{ fontSize: 11, fontWeight: 600, color: "#64748B", marginBottom: 6, textTransform: "uppercase", letterSpacing: ".06em" }}>{label}</div>}
      <div style={S.codeWrap}>
        <CopyButton text={code} />
        {code}
      </div>
    </div>
  );
}

function ParamTable({ params }) {
  if (!params || params.length === 0) return <div style={{ fontSize: 12, color: "#94A3B8", fontStyle: "italic", marginBottom: 16 }}>No request parameters required.</div>;
  return (
    <div style={{ border: "1px solid #E2E8F0", borderRadius: 10, overflow: "hidden", marginBottom: 16 }}>
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead>
          <tr>
            {["Parameter", "Type", "Required", "Description"].map(h => <th key={h} style={S.th}>{h}</th>)}
          </tr>
        </thead>
        <tbody>
          {params.map((p, i) => (
            <tr key={i}>
              <td style={S.td}><code style={{ color: "#185FA5", fontSize: 12 }}>{p.name}</code></td>
              <td style={S.td}><span style={{ color: "#64748B", fontSize: 12 }}>{p.type}</span></td>
              <td style={S.td}><span style={S.badge(p.req ? "required" : "optional")}>{p.req ? "required" : "optional"}</span></td>
              <td style={{ ...S.td, color: "#374151" }}>{p.desc}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ResponseTabs({ success, error }) {
  const [tab, setTab] = useState(0);
  const tabs = ["200 Success", "Error Response", "Error Code Reference"];
  return (
    <div>
      <div style={{ display: "flex", borderBottom: "1px solid #E2E8F0", marginBottom: 12, gap: 4 }}>
        {tabs.map((t, i) => (
          <button key={i} onClick={() => setTab(i)} style={S.tab(tab === i)}>{t}</button>
        ))}
      </div>
      {tab === 0 && <CodeBlock code={success} />}
      {tab === 1 && <CodeBlock code={error} />}
      {tab === 2 && (
        <div style={{ border: "1px solid #E2E8F0", borderRadius: 10, overflow: "hidden" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                {["Code", "Meaning", "Wallet Billed?"].map(h => <th key={h} style={S.th}>{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {ERROR_CODES.map(ec => (
                <tr key={ec.code}>
                  <td style={S.td}><code style={{ fontWeight: 700, color: ec.billed ? "#B91C1C" : "#374151" }}>{ec.code}</code></td>
                  <td style={{ ...S.td, color: "#374151" }}>{ec.meaning}</td>
                  <td style={S.td}>
                    <span style={{ ...S.badge(ec.billed ? "required" : "optional"), background: ec.billed ? "#FEF2F2" : "#F0FDF4", color: ec.billed ? "#B91C1C" : "#166534" }}>
                      {ec.billed ? "✓ Debited" : "✗ Free"}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function UrlCopyBar({ url }) {
  const [copied, setCopied] = useState(false);
  const full = `https://your-domain.com${url}`;
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 8,
      background: "#F8FAFC", border: "1px solid #E2E8F0", borderRadius: 8,
      padding: "8px 12px", marginBottom: 18, flexWrap: "wrap",
    }}>
      <span style={S.methodBadge}>POST</span>
      <code style={{ flex: 1, fontSize: 13, color: "#0F172A", wordBreak: "break-all" }}>{full}</code>
      <button
        onClick={() => { navigator.clipboard.writeText(full); setCopied(true); setTimeout(() => setCopied(false), 1800); }}
        style={{
          background: copied ? "#EAF3DE" : "#F1F5F9", border: "1px solid #CBD5E1",
          borderRadius: 5, padding: "4px 10px", fontSize: 11,
          color: copied ? "#3B6D11" : "#475569", cursor: "pointer", fontWeight: 500,
        }}
      >
        {copied ? "✓ Copied" : "Copy URL"}
      </button>
    </div>
  );
}

// ── Overview Page ─────────────────────────────────────────────────────────────
function OverviewPage() {
  return (
    <div>
      <h2 style={{ fontSize: 22, fontWeight: 700, color: "#0F172A", marginBottom: 4 }}>Architecture & Authentication</h2>
      <p style={{ fontSize: 13, color: "#64748B", marginBottom: 24 }}>
        Global configuration, authentication methods, and billing policies for the PlanAPI KYC Verification Suite.
      </p>

      <div style={{ background: "#EFF6FF", border: "1px solid #BFDBFE", borderRadius: 8, padding: "12px 16px", marginBottom: 20, fontSize: 13, color: "#1E40AF" }}>
        <strong>Base URL:</strong>&nbsp;
        <code style={{ background: "#DBEAFE", padding: "1px 6px", borderRadius: 4 }}>https://your-domain.com</code>
        &nbsp;·&nbsp;All endpoints use <strong>POST</strong> method.
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 24 }}>
        {[
          {
            title: "Option A — JWT / Session",
            color: "#185FA5", bg: "#EFF6FF",
            lines: [
              "Header: Authorization: Bearer <TOKEN>",
              "Body:   user_id=<int>",
              "Body:   pin=<4-digit PIN>",
            ],
          },
          {
            title: "Option B — PlanAPI Direct",
            color: "#3B6D11", bg: "#F0FDF4",
            lines: [
              "Header: TokenID: <TOKEN_ID>",
              "Header: ApiUserID: <USER_ID>",
              "Header: ApiPassword: <PASS>",
            ],
          },
        ].map(card => (
          <div key={card.title} style={{ background: card.bg, border: `1px solid ${card.color}30`, borderRadius: 10, padding: 16 }}>
            <div style={{ fontWeight: 700, color: card.color, marginBottom: 10, fontSize: 13 }}>{card.title}</div>
            {card.lines.map((l, i) => <div key={i} style={{ fontSize: 12, fontFamily: "monospace", color: "#374151", marginBottom: 4 }}>{l}</div>)}
          </div>
        ))}
      </div>

      <div style={{ fontSize: 13, fontWeight: 600, color: "#374151", marginBottom: 12 }}>Encoding by Endpoint Type</div>
      <div style={{ display: "flex", gap: 10, marginBottom: 24, flexWrap: "wrap" }}>
        {[
          { label: "Standard Endpoints", enc: "application/x-www-form-urlencoded or JSON" },
          { label: "OCR / Masking", enc: "multipart/form-data" },
        ].map(e => (
          <div key={e.label} style={{ flex: 1, minWidth: 200, background: "#F8FAFC", border: "1px solid #E2E8F0", borderRadius: 8, padding: "10px 14px" }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: "#64748B", marginBottom: 4 }}>{e.label}</div>
            <code style={{ fontSize: 11, color: "#4F46E5" }}>{e.enc}</code>
          </div>
        ))}
      </div>

      <div style={{ fontSize: 13, fontWeight: 600, color: "#374151", marginBottom: 12 }}>API Error Code Reference</div>
      <div style={{ border: "1px solid #E2E8F0", borderRadius: 10, overflow: "hidden" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>{["Errorcode", "Meaning", "Wallet Billed?"].map(h => <th key={h} style={S.th}>{h}</th>)}</tr>
          </thead>
          <tbody>
            {ERROR_CODES.map(ec => (
              <tr key={ec.code}>
                <td style={S.td}><code style={{ fontWeight: 700, color: ec.billed ? "#B91C1C" : "#374151", fontSize: 13 }}>{ec.code}</code></td>
                <td style={{ ...S.td, color: "#374151" }}>{ec.meaning}</td>
                <td style={S.td}>
                  <span style={{ ...S.badge(ec.billed ? "required" : "optional"), background: ec.billed ? "#FEF2F2" : "#F0FDF4", color: ec.billed ? "#B91C1C" : "#166534" }}>
                    {ec.billed ? "✓ Debited" : "✗ Not Billed"}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Endpoint Content ───────────────────────────────────────────────────────────
function EndpointPage({ mod }) {
  const curlExample = `curl -X POST "https://your-domain.com${mod.url}" \\
  -H "Authorization: Bearer YOUR_JWT_TOKEN" \\
  -H "Content-Type: ${mod.encoding || "application/json"}" \\
  -d '${mod.request}'`;

  return (
    <div>
      <h2 style={{ fontSize: 20, fontWeight: 700, color: "#0F172A", marginBottom: 4 }}>{mod.label}</h2>
      <p style={{ fontSize: 13, color: "#64748B", marginBottom: 16 }}>
        Method: <span style={S.methodBadge}>POST</span>
        {mod.encoding && <>&nbsp;&nbsp;<span style={S.encodingBadge}>{mod.encoding}</span></>}
      </p>

      <UrlCopyBar url={mod.url} />

      {mod.params && mod.params.length > 0 && (
        <>
          <div style={{ fontSize: 11, fontWeight: 700, color: "#64748B", textTransform: "uppercase", letterSpacing: ".07em", marginBottom: 8 }}>Parameters</div>
          <ParamTable params={mod.params} />
        </>
      )}

      <CodeBlock code={curlExample} label="cURL Example" />
      <div style={{ fontSize: 11, fontWeight: 700, color: "#64748B", textTransform: "uppercase", letterSpacing: ".07em", marginBottom: 8 }}>Responses</div>
      <ResponseTabs success={mod.success} error={mod.error} />
    </div>
  );
}

// ── Gateway Page ────────────────────────────────────────────────────────────
function GatewayPage({ mod }) {
  return (
    <div>
      <h2 style={{ fontSize: 20, fontWeight: 700, color: "#0F172A", marginBottom: 4 }}>Unified KYC Gateway</h2>
      <p style={{ fontSize: 13, color: "#64748B", marginBottom: 16 }}>
        Single entry-point for all 35+ verification types. Specify the <code>service</code> or <code>kyc_type</code> to route dynamically.
      </p>

      <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
        <div style={{ background: "#FFF7ED", border: "1px solid #FED7AA", borderRadius: 8, padding: "10px 14px", fontSize: 12, color: "#92400E", flex: 1, minWidth: 200 }}>
          <strong>API Partner Mode:</strong> Flat ₹4.72 fee — pure pass-through verification
        </div>
        <div style={{ background: "#EFF6FF", border: "1px solid #BFDBFE", borderRadius: 8, padding: "10px 14px", fontSize: 12, color: "#1E40AF", flex: 1, minWidth: 200 }}>
          <strong>Retailer Mode:</strong> Wallet deduction + name match on result
        </div>
      </div>

      <UrlCopyBar url={mod.url} />
      <ParamTable params={mod.params} />
      <CodeBlock code={mod.request} label="Request Body (JSON)" />
      <ResponseTabs success={mod.success} error={mod.error} />

      <div style={{ marginTop: 20 }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: "#374151", marginBottom: 10 }}>Common Service Keys</div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
          {["PAN_VERIFY", "AADHAAR_OTP", "BANK_VERIFY", "UPI_VERIFY", "GST_VERIFY",
            "VOTER_BASIC", "DL_VERIFY", "RC_VERIFY", "PASSPORT_VERIFY", "TAN_VERIFY",
            "MCA_SEARCH", "UDYOG_VERIFY", "DIGILOCKER_INIT"].map(key => (
            <span key={key} style={{
              background: "#F1F5F9", border: "1px solid #CBD5E1", borderRadius: 5,
              padding: "3px 8px", fontSize: 11, fontFamily: "monospace", color: "#374151",
            }}>{key}</span>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────
export default function KYCApiDocs() {
  const [activeId, setActiveId] = useState("overview");
  const [search, setSearch] = useState("");

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return MODULES.filter(m => !q || m.label.toLowerCase().includes(q) || (m.url || "").toLowerCase().includes(q));
  }, [search]);

  const active = MODULES.find(m => m.id === activeId) || MODULES[0];

  return (
    <main style={{ minHeight: "100vh", background: "#F8FAFC", padding: "20px 0", fontFamily: "'Inter', system-ui, sans-serif" }}>
      <div style={{ maxWidth: 1280, margin: "0 auto", padding: "0 16px" }}>

        {/* Header */}
        <div style={{
          background: "#fff", borderRadius: "12px 12px 0 0",
          borderBottom: "1px solid #E2E8F0",
          padding: "16px 24px", display: "flex", alignItems: "center",
          justifyContent: "space-between", flexWrap: "wrap", gap: 12,
        }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, color: "#0F172A", display: "flex", alignItems: "center", gap: 10 }}>
              <span style={{ fontSize: 20 }}>🔐</span>
              PlanAPI KYC Verification Suite
              <span style={{ fontSize: 10, background: "#DBEAFE", color: "#1D4ED8", padding: "2px 8px", borderRadius: 4, fontWeight: 700 }}>v2.0 Spec</span>
            </div>
            <div style={{ fontSize: 12, color: "#64748B", marginTop: 2 }}>
              Interactive API Reference — 13 Modules · 35+ Endpoints
            </div>
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <span style={{ background: "#DCFCE7", color: "#166534", fontSize: 11, fontWeight: 600, padding: "4px 10px", borderRadius: 20 }}>● Live</span>
          </div>
        </div>

        {/* Body */}
        <div style={{ background: "#fff", border: "1px solid #E2E8F0", borderTop: "none", borderRadius: "0 0 12px 12px", display: "flex", minHeight: 700 }}>

          {/* Sidebar */}
          <div style={{ width: 270, flexShrink: 0, borderRight: "1px solid #E2E8F0", display: "flex", flexDirection: "column" }}>
            <div style={{ padding: "12px 12px 8px" }}>
              <div style={{ position: "relative" }}>
                <span style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", fontSize: 13, color: "#94A3B8" }}>🔍</span>
                <input
                  placeholder="Search endpoints..."
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  style={{
                    width: "100%", padding: "7px 10px 7px 30px", borderRadius: 7,
                    border: "1px solid #E2E8F0", fontSize: 12, color: "#374151",
                    background: "#F8FAFC", outline: "none", boxSizing: "border-box",
                  }}
                />
              </div>
            </div>
            <div style={{ flex: 1, overflowY: "auto", paddingBottom: 16 }}>
              {filtered.map(item => (
                <React.Fragment key={item.id}>
                  {item.section && (
                    <div style={S.sectionLabel}>{item.section}</div>
                  )}
                  <div
                    onClick={() => setActiveId(item.id)}
                    style={{
                      display: "flex", alignItems: "center", gap: 9,
                      padding: "8px 14px", cursor: "pointer",
                      borderLeft: `3px solid ${activeId === item.id ? item.color : "transparent"}`,
                      background: activeId === item.id ? `${item.color}12` : "transparent",
                      color: activeId === item.id ? item.color : "#64748B",
                      fontWeight: activeId === item.id ? 600 : 400,
                      fontSize: 13, transition: "all .12s",
                    }}
                  >
                    <span style={{ width: 7, height: 7, borderRadius: "50%", background: item.color, flexShrink: 0, opacity: activeId === item.id ? 1 : 0.35 }} />
                    <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{item.label}</span>
                  </div>
                </React.Fragment>
              ))}
              {filtered.length === 0 && (
                <div style={{ padding: "20px 16px", textAlign: "center", color: "#94A3B8", fontSize: 12 }}>No endpoints match your search.</div>
              )}
            </div>
          </div>

          {/* Content */}
          <div style={{ flex: 1, padding: "28px 32px", overflowX: "auto", maxWidth: 920 }}>
            {active.content === "overview" && <OverviewPage />}
            {active.content === "gateway" && <GatewayPage mod={active} />}
            {active.content === "endpoint" && <EndpointPage mod={active} />}
          </div>
        </div>
      </div>
    </main>
  );
}
